import { useRef, useCallback, useEffect } from 'react';
import { createServer } from 'vite';
import type { InlineConfig, ViteDevServer } from 'vite';
import { useDevCenterClient } from '@wix/dev-center-client';
import { useErrorReporter } from '@wix/cli-error-reporting';
import { CliError, CliErrorCode } from '@wix/cli-error';
import {
  getBackendViteConfig,
  getDevClientViteConfig,
  getDevSiteComponentsConfig,
} from '../../vite/index.js';
import { createProxyDevServersPlugin } from '../../vite/plugins/proxy-dev-servers.js';
import { getViteServerUrl } from '../../vite/vite-server-url.js';
import type { ProjectModelWatcher } from '../../app-manifest/project-model-watcher.js';
import { useProjectModel } from '../../model.js';
import type { Logger } from '../../vite/logger.js';

interface Options {
  projectModelWatcher: ProjectModelWatcher;
  customLogger: Logger;
  baseBackendUrl?: string;
}

function useViteDevServer<T>({
  createConfig,
}: {
  createConfig: (options: T) => Promise<InlineConfig> | InlineConfig;
}) {
  const { reportError } = useErrorReporter();
  const devServerRef = useRef<ViteDevServer>();

  useEffect(() => {
    return () => {
      devServerRef.current?.close().catch(reportError);
    };
  }, [reportError]);

  return useCallback(
    async (options: T) => {
      try {
        const config = await createConfig(options);
        const devServer = await createServer(config);

        devServerRef.current = devServer;

        await devServer.listen();

        return { devServerUrl: getViteServerUrl(devServer), devServer };
      } catch (error) {
        if (
          error instanceof Error &&
          'code' in error &&
          error.code === 'EACCES'
        ) {
          throw new CliError({
            code: CliErrorCode.EAccessPermissionDenied(),
            cause: error,
          });
        } else {
          throw error;
        }
      }
    },
    [createConfig]
  );
}

export function useClientDevServer({
  https,
  port,
  origin,
}: {
  https: boolean;
  port?: number;
  origin?: string;
}) {
  const { model } = useProjectModel();

  const { applyProxyDevServers, registerProxyDevServer } =
    createProxyDevServersPlugin();

  const createDevServer = useViteDevServer<Options>({
    createConfig: ({ projectModelWatcher, baseBackendUrl, customLogger }) => {
      const config = getDevClientViteConfig(model, {
        https,
        projectModelWatcher,
        customLogger,
        baseBackendUrl,
        port,
        origin,
      });
      config.plugins = [...(config.plugins ?? []), applyProxyDevServers()];

      return config;
    },
  });

  const createSiteComponentsDevServer = useViteDevServer<Options>({
    createConfig: ({ projectModelWatcher, baseBackendUrl, customLogger }) => {
      return getDevSiteComponentsConfig(model, {
        https,
        projectModelWatcher,
        customLogger,
        baseBackendUrl,
        port,
        origin,
      });
    },
  });

  return async (opts: Options) => {
    const clientServer = await createDevServer(opts);
    const siteComponentsServer = await createSiteComponentsDevServer(opts);

    registerProxyDevServer(siteComponentsServer.devServer);

    return clientServer;
  };
}

export function useBackendDevServer() {
  const { model } = useProjectModel();
  const { getAppSecret } = useDevCenterClient();

  const createDevServer = useViteDevServer<Options>({
    createConfig: async ({ customLogger }) => {
      const appSecret = await getAppSecret({
        appId: model.config.appId,
      });

      return getBackendViteConfig(model, { customLogger }, appSecret);
    },
  });

  return createDevServer;
}
